import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function uid(prefix = 'id'): string {
  // 9-char base36 token; collision-safe enough for client-side IDs.
  const rand = Math.random().toString(36).slice(2, 8);
  const time = Date.now().toString(36).slice(-3);
  return `${prefix}-${time}${rand}`;
}

/**
 * Strips a trailing inline option list from an MCQ question. Generators
 * sometimes bake the choices into the question text — e.g.
 *   "Which property … wires? (a) Malleability (b) Ductility (c) Sonority (d) Lustre"
 * — which then renders twice (once in the question, once as the A/B/C/D rows).
 *
 * We cut the question at the first option marker, but only when it's safe:
 *   - there are at least two markers, and the run starts at "a"/"1", and
 *   - (when `options` is given) the tail actually contains the option texts,
 * so a legitimate parenthetical like "f(a) and g(b)" is never touched.
 */
export function stripInlineMcqOptions(question: string, options?: string[]): string {
  if (!question) return question;

  // Markers like "(a)", "a)", "a.", "(1)", "1)" — letters a–h or digits 1–8.
  const markerRe = /[([]?\s*([a-hA-H1-8])\s*[).\]]\s*/g;
  const matches = [...question.matchAll(markerRe)];
  if (matches.length < 2) return question;

  const firstLabel = matches[0][1].toLowerCase();
  if (firstLabel !== 'a' && firstLabel !== '1') return question;

  const cutAt = matches[0].index ?? -1;
  if (cutAt <= 0) return question;

  // Precise guard: only strip when the tail really lists the known options.
  if (options && options.length) {
    const tailLc = question.slice(cutAt).toLowerCase();
    const present = options.filter(
      (o) => o && tailLc.includes(o.trim().toLowerCase()),
    ).length;
    if (present < Math.min(2, options.length)) return question;
  }

  const head = question.slice(0, cutAt).replace(/[\s:：—–-]+$/, '').trim();
  return head.length >= 3 ? head : question;
}

export function debounce<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  wait: number,
): ((...args: TArgs) => void) & { flush: () => void; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: TArgs | null = null;

  const debounced = (...args: TArgs) => {
    lastArgs = args;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      if (lastArgs) fn(...lastArgs);
    }, wait);
  };

  debounced.flush = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
      if (lastArgs) fn(...lastArgs);
    }
  };
  debounced.cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    lastArgs = null;
  };

  return debounced;
}
